import { supabase } from './supabaseClient';
import { parseProjectMilestoneWorkbook } from '../utils/excelParser';
import { computeUploadSummaries } from '../utils/analytics';

const CHUNK_SIZE = 500;

function missingColumnFromError(error) {
  const message = String(error?.message || error?.details || '');
  const patterns = [
    /Could not find the ['"]([^'"]+)['"] column/i,
    /column ['"]([^'"]+)['"] of relation/i,
    /column ([a-zA-Z0-9_]+) does not exist/i,
  ];
  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match?.[1]) return match[1];
  }
  return '';
}

async function insertChunkWithMissingColumnRetry(table, chunk) {
  let workingChunk = chunk;
  const removedColumns = new Set();

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const { error } = await supabase.from(table).insert(workingChunk);
    if (!error) return;

    const missingColumn = missingColumnFromError(error);
    if (table !== 'project_milestone_clean' || !missingColumn || removedColumns.has(missingColumn)) {
      throw error;
    }

    removedColumns.add(missingColumn);
    workingChunk = workingChunk.map((row) => {
      const next = { ...row };
      delete next[missingColumn];
      return next;
    });
  }

  throw new Error(`Unable to insert ${table} after removing missing columns: ${[...removedColumns].join(', ')}`);
}

async function insertChunks(table, rows, uploadId) {
  for (let index = 0; index < rows.length; index += CHUNK_SIZE) {
    const chunk = rows.slice(index, index + CHUNK_SIZE).map((row) => ({ ...row, upload_id: uploadId }));
    await insertChunkWithMissingColumnRetry(table, chunk);
  }
}

async function insertSummaryChunks(table, rows, uploadId) {
  if (!rows.length) return;
  await insertChunks(table, rows, uploadId);
}


export async function cleanupOldUploads(keepLatest) {
  const args = keepLatest ? { p_keep_latest: Number(keepLatest) } : undefined;
  const { data, error } = await supabase.rpc('cleanup_old_uploads', args);
  if (error) throw error;
  return data;
}

export async function uploadProjectMilestoneReport(file, user, mapping) {
  if (!file) throw new Error('Please choose an .xlsx file.');
  if (!file.name.toLowerCase().endsWith('.xlsx')) throw new Error('Only .xlsx files are supported.');

  const parsed = await parseProjectMilestoneWorkbook(file, mapping);

  const uploadPayload = {
    file_name: file.name,
    file_size_bytes: file.size,
    uploaded_by: user.id,
    uploaded_by_email: user.email,
    status: 'processing',
    is_active: false,
    total_rows: parsed.totalRowsFound,
    valid_rows: parsed.validRowsImported,
    skipped_rows: parsed.skippedRows,
    min_milestone_date: parsed.minDate,
    max_milestone_date: parsed.maxDate,
    processing_summary: {
      sheetName: parsed.sheetName,
      headerRowNumber: parsed.headerRowNumber,
      totalRowsFound: parsed.totalRowsFound,
      validRowsImported: parsed.validRowsImported,
      skippedRows: parsed.skippedRows,
      uniqueClients: parsed.uniqueClients,
      uniqueStates: parsed.uniqueStates,
      uniqueTeamMembersByRole: parsed.uniqueTeamMembersByRole,
      mapping: parsed.mapping,
    },
  };

  const { data: upload, error: uploadError } = await supabase
    .from('uploads')
    .insert(uploadPayload)
    .select()
    .single();

  if (uploadError) throw uploadError;

  try {
    await insertChunks('project_milestone_raw', parsed.rawRows, upload.id);
    await insertChunks('project_milestone_clean', parsed.cleanRows, upload.id);

    const summaries = computeUploadSummaries(parsed.cleanRows);
    await insertSummaryChunks('dashboard_summary', summaries.dashboardSummary, upload.id);
    await insertSummaryChunks('team_member_performance_summary', summaries.teamSummary, upload.id);
    await insertSummaryChunks('state_summary', summaries.stateSummary, upload.id);
    await insertSummaryChunks('client_summary', summaries.clientSummary, upload.id);

    const { error: completeError } = await supabase
      .from('uploads')
      .update({ status: 'completed', error_message: null })
      .eq('id', upload.id);
    if (completeError) throw completeError;

    const { error: activateError } = await supabase.rpc('activate_upload', { p_upload_id: upload.id });
    if (activateError) throw activateError;

    try {
      await cleanupOldUploads();
    } catch (cleanupError) {
      console.warn('Upload cleanup skipped. Run the latest Supabase migration to enable cleanup_old_uploads.', cleanupError);
    }

    return { upload: { ...upload, status: 'completed', is_active: true }, parsed };
  } catch (error) {
    await supabase
      .from('uploads')
      .update({ status: 'failed', error_message: error.message })
      .eq('id', upload.id);
    throw error;
  }
}

export async function getUploads() {
  const { data, error } = await supabase
    .from('uploads')
    .select('*')
    .order('uploaded_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getActiveUpload() {
  const { data, error } = await supabase
    .from('uploads')
    .select('*')
    .eq('is_active', true)
    .eq('status', 'completed')
    .order('uploaded_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function activateUpload(uploadId) {
  const { error } = await supabase.rpc('activate_upload', { p_upload_id: uploadId });
  if (error) throw error;
}


export async function deleteUpload(uploadId) {
  if (!uploadId) throw new Error('Missing upload ID.');

  const { error } = await supabase.rpc('delete_upload', { p_upload_id: uploadId });
  if (error) throw error;
}
